import { useRef } from 'react';
import { mergeProps, useField } from 'react-aria';

import type { FieldPropsWithElement } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { styled } from '../../stitches.config';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { labelAsText } from '../../utils/labelAsText';
import { useFieldElement } from './common/useFieldElement';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';

export const FloatingFieldAlert = styled(Alert, {
  minWidth: '$220',
});
FloatingFieldAlert.displayName = 'FloatingFieldAlert';

// Many controls (TextInput, ComboBox, …) drop the tooltip trigger's hover handlers, so the
// trigger is a box-less wrapper they bubble through; the popup anchors to the control itself.
const FloatingFieldTrigger = styled('div', {
  display: 'contents',
});
FloatingFieldTrigger.displayName = 'FloatingFieldTrigger';

export function FloatingField(props: FieldPropsWithElement) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();

  const {
    label = customFieldProps.label,
    description = customFieldProps.description,
    errorMessage = customFieldProps.errorMessage,
    element,
  } = props;

  const { fieldProps: inputProps, errorMessageProps } = useField({
    // Floating fields sit inline in tables and compact rows and deliberately render
    // no visible label, so handing `label` to react-aria pointed the control's
    // `aria-labelledby` at an element that never existed — leaving it with no
    // accessible name at all. Naming it directly is what the absent label calls for.
    // The description and error ids need no such care: react-aria drops those when
    // nothing claims them.
    'aria-label': labelAsText(label),
    description,
    errorMessage,
  });

  const control = useFieldElement(element, mergeProps(inputProps, customInputProps));
  const triggerRef = useRef<HTMLDivElement>(null);

  return (
    <Tooltip
      tight
      anchor={() => triggerRef.current?.firstElementChild ?? null}
      closeOnClick={false}
      contents={
        errorMessage ? (
          <FloatingFieldAlert
            {...errorMessageProps}
            type="inline"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        ) : undefined
      }
    >
      <FloatingFieldTrigger ref={triggerRef}>{control}</FloatingFieldTrigger>
    </Tooltip>
  );
}
